import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Input,
  TimePicker,
  Form,
  Space,
  message,
  Modal,
} from "antd";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  nurseDailyRoutineApi,
  updateDailyRoutineApi,
} from "../../../../../services/nurse.services";
import useQuery from "../../../../../hooks/useQuery";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../Doctor/InitialInspection/ClosePopup";
import ExpandableRemarks from "../ExpandableRemarks";
import { getCareTimePickerProps } from "../nurseCareMobilePickerProps";
import { withCareSorters } from "../careTableSorting";
const format = "hh:mm A";
const getDisabledTime = () => {
  const now = dayjs();

  return {
    disabledHours: () => {
      const hours = [];
      for (let i = now.hour() + 1; i < 24; i++) {
        hours.push(i);
      }
      return hours;
    },

    disabledMinutes: (selectedHour) => {
      if (selectedHour === now.hour()) {
        const minutes = [];
        for (let i = now.minute() + 1; i < 60; i++) {
          minutes.push(i);
        }
        return minutes;
      }
      return [];
    },

    disabledSeconds: () => [], 
  };
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  form,
  selectOptions,
  isMobile,
  ...restProps
}) => {
  useEffect(() => {
    if (editable) {
      form.setFieldsValue({
        [dataIndex]:
          dataIndex === "process_time" || dataIndex === "action_time"
            ? record[dataIndex]
              ? dayjs(record[dataIndex])
              : null
            : record[dataIndex],
      });
    }
  }, [editable, record, dataIndex, form]);

  const inputNode =
    dataIndex === "remarks" ? (
      <Input.TextArea />
    ) : (
      (dataIndex === "process_time" || dataIndex === "action_time") && (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: "Time Done is required!",
            },
          ]}
        >
          <TimePicker
            format={format}
            needConfirm={false}
            inputReadOnly
            disabledTime={getDisabledTime}
            className="nurse-care-time-input"
            {...getCareTimePickerProps(isMobile)}
          />
        </Form.Item>
      )
    );

  return (
    <td
      {...restProps}
      style={{
        wordBreak: "break-word",
        whiteSpace: "normal",
        maxWidth: dataIndex === "remarks" ? "280px" : "150px",
      }}
    >
      {editable ? (
        <Form.Item name={dataIndex} style={{ margin: 0 }}>
          {inputNode}
        </Form.Item>
      ) : dataIndex === "process_time" || dataIndex === "action_time" ? (
        record[dataIndex] ? (
          dayjs(record[dataIndex])?.format(format)
        ) : null
      ) : dataIndex === "remarks" ? (
        <ExpandableRemarks text={record[dataIndex]} />
      ) : (
        children
      )}
    </td>
  );
};

const DailyRoutineFormTable = ({ nurseData, type, getData }) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const isActive = params?.status === "current";
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (nurseData) {
      const updatedData = nurseData?.map((item, index) => ({
        ...item,
        key: index.toString(),
      }));
      setDataSource(updatedData);
    }
  }, [nurseData]);

  const isEditing = (record) => record.key === editingKey;

  const handleAdd = () => {
  setEditData(null);
  setModalOpen(true);
};

  const handleDelete = (key) => {
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
  };

  const handleSave = async (record) => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const row = await form.validateFields();
      // Check if process_time is empty
      if (!row.process_time) {
        throw new Error("Time Done is required!");
      }
      const updatedRecord = { ...row, daily_routine_type: type };
      if (record?.id) {
        const res = await updateDailyRoutineApi(record?.id, updatedRecord);
        await getData();
        message.success(res?.data?.message);
        setEditingKey("");
      } else {
        const res = await nurseDailyRoutineApi(params?.patient, updatedRecord);
        await getData();
        message.success(res?.data?.message);
        setEditingKey("");
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(
        err?.response?.data?.message || err.message || "Failed to save."
      );
      // The server refused the entry, so the row goes back to what is stored
      // rather than keeping the rejected value on screen.
      if (err?.response) {
        form.setFieldsValue({
          ...record,
          process_time: record?.process_time
            ? dayjs(record.process_time)
            : null,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };
  const sosModalTitle = type
    ?.split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const handleModalSubmit = async (values) => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        daily_routine_type: type,
      };
      const res = editData?.id
        ? await updateDailyRoutineApi(editData.id, payload)
        : await nurseDailyRoutineApi(params?.patient, payload);
      await getData();
      message.success(res?.data?.message);
      setModalOpen(false);
      setEditData(null);
    } catch (err) {
      message.error(err?.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openMobileEditModal = (record) => {
    setEditData(record);
    setModalOpen(true);
  };
  const columns = [
    // {
    //   title: "Schedule",
    //   dataIndex: "action_time",
    //   key: "action_time",
    //   editable: true,
    // },

    {
      title: "Time Done",
      dataIndex: "process_time",
      key: "process_time",
      editable: true,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      editable: true,
      width: 280,
      render: (text) => <ExpandableRemarks text={text} />,
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
    },
    ...(isActive
      ? [
          {
            title: "Action",
            render: (_, record) => {
              const editable = !isMobile && isEditing(record);
              return (
                <Space>
                  <Button
                    loading={submitting && (isMobile || isEditing(record))}
                    disabled={submitting}
                    onClick={() => {
                      if (isMobile) {
                        openMobileEditModal(record);
                        return;
                      }
                      if (editable) {
                        handleSave(record);
                      } else {
                        setEditingKey(record.key);
                        form.setFieldsValue({
                          ...record,
                          process_time: record.process_time
                            ? dayjs(record.process_time)
                            : null,
                        });
                      }
                    }}
                  >
                    {editable ? "Save" : "Edit"}
                  </Button>
                  {record?.scheduleDisplay && (
                    <Button
                      icon={<DeleteOutlined style={{ color: "red" }} />}
                      onClick={() => handleDelete(record.key)}
                    />
                  )}
                  {editable && !record?.scheduleDisplay && (
                    <CloseCircleOutlined onClick={() => setEditingKey("")} />
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  const mergedColumns = withCareSorters(columns, dataSource).map((col) => ({
    ...col,
    onCell: (record) => ({
      record,
      editable: col.editable && isEditing(record),
      dataIndex: col.dataIndex,
      title: col.title,
      selectOptions: col.selectOptions,
      form,
      isMobile,
    }),
  }));

  return (
    <Form form={form} component={false}>
      <Row>
        <Col span={24} style={{ textAlign: "right", marginBottom: 10 }}>
          {isActive && editingKey == "" && (
            <Button
              className="inv-btn"
              type="primary"
              onClick={handleAdd}
              style={{ fontWeight: 500 }}
            >
              <PlusOutlined /> SOS
            </Button>
          )}
        </Col>
        <Col span={24}>
          <div className="nurse-care-table-scroll">
            <Table
              className="nurse-care-data-table"
              pagination={false}
              columns={mergedColumns}
              dataSource={dataSource}
              tableLayout="auto"
              scroll={{ x: 720 }}
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
            />
          </div>
        </Col>
      </Row>
      {modalOpen && (
        <Modal
          title={
            editData?.id
              ? `Edit ${sosModalTitle}`
              : `${sosModalTitle} - SOS`
          }
          width={isMobile ? "calc(100vw - 32px)" : 440}
          centered
          wrapClassName={isMobile ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
          getContainer={() => document.body}
          open={modalOpen}
          footer={null}
          destroyOnClose
          onCancel={() => {
            setModalOpen(false);
            setEditData(null);
          }}
        >
          <Form
            layout="vertical"
            onFinish={handleModalSubmit}
            initialValues={
              editData
                ? {
                    ...editData,
                    process_time: editData.process_time
                      ? dayjs(editData.process_time)
                      : dayjs(),
                  }
                : {
                    process_time: dayjs(),
                  }
            }
          >
            <Form.Item
              label="Time Done"
              name="process_time"
              rules={[
                {
                  required: true,
                  message: "Time done is required!",
                },
              ]}
            >
              <TimePicker
                inputReadOnly
                format={format}
                needConfirm={false}
                disabledTime={getDisabledTime}
                style={{ width: "100%" }}
                {...getCareTimePickerProps(isMobile)}
              />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={3} placeholder="Add remarks (optional)" />
            </Form.Item>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 8,
              }}
            >
              <Button
                onClick={() => {
                  setModalOpen(false);
                  setEditData(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                Save
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </Form>
  );
};

export default DailyRoutineFormTable;
